// Les gestes d'édition de l'ABS, en fonctions pures : chacune rend un NOUVEAU document et laisse
// celui qu'elle reçoit intact (l'état React compare les références, et le document enregistré sert
// de point de départ à « Annuler »).
//
// La règle des chiffres modifiés est celle du contrat (schema.tracer_modifications puis
// verifier_modifications, décision de Marc du 14/10) : un chiffre se change — valeur, texte, unité,
// source, millésime — avec une justification d'au moins 10 caractères ; son origine reste
// « calcul ». Le client n'envoie que la nouvelle valeur et `modification: { justification }` : le
// serveur pose ce que le calcul avait produit, l'auteur et la date (jamais lus dans la requête), et
// une cellule revenue à son état d'origine perd sa trace. L'origine se lit donc dans le document
// ENREGISTRÉ, jamais dans la saisie en cours.
import { JUSTIFICATION_MIN, type Bloc, type BlocACompleter, type Cellule, type Document, type Source } from './types'

/** L'adresse stable d'une cellule, celle du contrat (`_cellules_par_adresse`) : le bloc, puis
    l'item (chiffres clés) ou la ligne (tableau), puis « valeur » / « comparaison » ou la colonne. */
export interface Adresse { bloc: string; cle: string; champ: string }
/** Une nouvelle valeur ; unité, source et millésime restent ceux de la cellule s'ils sont absents. */
export interface Saisie {
  valeur?: number | null
  texte?: string | null
  unite?: string | null
  source?: string | null
  millesime?: string | null
  decimales?: number
  /** une source à déclarer dans `Document.sources` avant de la citer (le CCAS, une étude locale…) */
  nouvelleSource?: Source
}
/** L'état d'un chiffre au sens du contrat (`_ORIGINE_CHAMPS`). */
export interface Etat { valeur: number | null; texte: string | null; unite: string | null; source: string | null;
                        millesime: string | null; decimales: number }

export function celluleA(doc: Document, adr: Adresse): Cellule | undefined {
  for (const s of doc.sections) {
    for (const b of s.blocs ?? []) {
      if (b.id !== adr.bloc) continue
      if (b.type === 'chiffres_cles') {
        const it = b.items.find(i => i.id === adr.cle)
        if (!it) return undefined
        if (adr.champ === 'valeur') return it.cellule
        if (adr.champ === 'comparaison') return it.comparaison ?? undefined
        return undefined
      }
      if (b.type === 'tableau') return b.lignes.find(l => l.id === adr.cle)?.cellules[adr.champ]
      return undefined
    }
  }
  return undefined
}

function remplacerBloc(doc: Document, id: string, f: (b: Bloc) => Bloc): Document {
  let trouve = false
  const sections = doc.sections.map(s => {
    if (!(s.blocs ?? []).some(b => b.id === id)) return s
    trouve = true
    return { ...s, blocs: (s.blocs ?? []).map(b => (b.id === id ? f(b) : b)) }
  })
  if (!trouve) throw new Error(`bloc introuvable : ${id}`)
  return { ...doc, sections }
}

function remplacerCellule(doc: Document, adr: Adresse, f: (c: Cellule) => Cellule): Document {
  if (!celluleA(doc, adr)) throw new Error(`cellule introuvable : ${adr.bloc}/${adr.cle}/${adr.champ}`)
  return remplacerBloc(doc, adr.bloc, b => {
    if (b.type === 'chiffres_cles') {
      return { ...b, items: b.items.map(it => {
        if (it.id !== adr.cle) return it
        return adr.champ === 'valeur' ? { ...it, cellule: f(it.cellule) } : { ...it, comparaison: f(it.comparaison!) }
      }) }
    }
    if (b.type === 'tableau') {
      return { ...b, lignes: b.lignes.map(l => (l.id !== adr.cle ? l
        : { ...l, cellules: { ...l.cellules, [adr.champ]: f(l.cellules[adr.champ]) } })) }
    }
    return b
  })
}

export function etatDe(c: Cellule): Etat {
  return { valeur: c.valeur ?? null, texte: c.texte?.trim() ? c.texte : null, unite: c.unite ?? null,
           source: c.source ?? null, millesime: c.millesime ?? null, decimales: c.decimales ?? 0 }
}

const memeEtat = (a: Etat, b: Etat) => a.valeur === b.valeur && a.texte === b.texte && a.unite === b.unite
  && a.source === b.source && a.millesime === b.millesime && a.decimales === b.decimales

/** Ce que le calcul avait produit, lu sur une cellule ENREGISTRÉE : la trace posée par le serveur si
    elle existe, sinon la cellule elle-même. */
export function origineDe(enregistree: Cellule): Etat {
  const m = enregistree.modification
  if (!m) return etatDe(enregistree)
  return { valeur: m.valeur_origine ?? null, texte: m.texte_origine ?? null, unite: m.unite_origine ?? null,
           source: m.source_origine ?? null, millesime: m.millesime_origine ?? null,
           decimales: m.decimales_origine ?? 0 }
}

/** Une trace posée par le serveur (et non une justification en attente d'enregistrement). */
export const traceServeur = (c: Cellule) => !!c.modification?.le

export function justificationValide(j: string): boolean {
  return j.trim().length >= JUSTIFICATION_MIN
}

/** Modifie un chiffre. `enregistre` est le document tel que le serveur l'a rendu : il dit l'origine.
    Revenir à l'origine retire la trace ; sinon la cellule porte `modification: { justification }`
    (ou garde la trace du serveur si rien n'a changé depuis l'enregistrement). */
export function modifierCellule(doc: Document, enregistre: Document, adr: Adresse, saisie: Saisie,
                                justification: string): Document {
  if (saisie.nouvelleSource) {
    const { nouvelleSource, ...reste } = saisie
    const [avecSource, cle] = declarerSource(doc, nouvelleSource)
    return modifierCellule(avecSource, enregistre, adr, { ...reste, source: cle }, justification)
  }
  return remplacerCellule(doc, adr, c => {
    const reference = celluleA(enregistre, adr) ?? c
    const origine = origineDe(reference)
    const nouvel: Etat = {
      valeur: saisie.valeur ?? null, texte: saisie.texte ?? null,
      unite: saisie.unite !== undefined ? saisie.unite || null : c.unite ?? null,
      source: saisie.source !== undefined ? saisie.source || null : c.source ?? null,
      millesime: saisie.millesime !== undefined ? saisie.millesime || null : c.millesime ?? null,
      decimales: saisie.decimales ?? c.decimales ?? 0,
    }
    if (memeEtat(nouvel, origine)) return { ...c, ...nouvel, modification: null }
    if (!justificationValide(justification)) {
      throw new Error(`La justification compte au moins ${JUSTIFICATION_MIN} caractères.`)
    }
    const j = justification.trim()
    const inchangee = reference.modification && memeEtat(nouvel, etatDe(reference))
      && reference.modification.justification === j
    return { ...c, ...nouvel, modification: inchangee ? reference.modification : { justification: j } }
  })
}

/** Déclare une source dans `Document.sources` (une cellule ne cite qu'une source déclarée) ; rend le
    document et la clé, unique, de la source. */
export function declarerSource(doc: Document, source: Source): [Document, string] {
  const base = 'ajout-' + (source.libelle.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'source')
  let cle = base
  for (let i = 2; cle in doc.sources; i++) cle = `${base}-${i}`
  return [{ ...doc, sources: { ...doc.sources, [cle]: source } }, cle]
}

/** Le contenu d'un texte ou d'une alerte. Un texte changé par un humain prend l'origine « humain »
    (le serveur le pose aussi) ; son statut ne change pas — « Valider ce texte » le fait. */
export function modifierTexte(doc: Document, id: string, contenu: string): Document {
  return remplacerBloc(doc, id, b => {
    if (b.type !== 'texte' && b.type !== 'alerte') throw new Error(`${id} n'est pas un texte`)
    if ((b.contenu ?? '') === contenu) return b
    return { ...b, contenu, origine: 'humain' }
  })
}

export function validerTexte(doc: Document, id: string): Document {
  return remplacerBloc(doc, id, b => {
    if (b.type !== 'texte' && b.type !== 'alerte') throw new Error(`${id} n'est pas un texte`)
    return { ...b, statut: 'valide' }
  })
}

export function basculerSection(doc: Document, id: string): Document {
  if (!doc.sections.some(s => s.id === id)) throw new Error(`section introuvable : ${id}`)
  return { ...doc, sections: doc.sections.map(s => (s.id === id ? { ...s, masquee: !s.masquee } : s)) }
}

/** Une donnée du CCAS, saisie par l'équipe : origine « ccas », d'où elle vient obligatoire. */
export function completerDonnee(doc: Document, id: string,
                                donnee: Saisie & { unite?: string | null; decimales?: number },
                                sourceLibre: string): Document {
  if (!sourceLibre.trim()) throw new Error('Indiquez la source de la donnée (document, service, date).')
  if ((donnee.valeur ?? null) === null && !(donnee.texte ?? '').trim()) {
    throw new Error('Saisissez une valeur ou un texte.')
  }
  return remplacerBloc(doc, id, b => {
    if (b.type !== 'a_completer') throw new Error(`${id} n'est pas un bloc à compléter`)
    const cellule: Cellule = {
      valeur: donnee.valeur ?? null, texte: (donnee.texte ?? '').trim() || null,
      unite: (donnee.unite ?? '').trim() || null, decimales: donnee.decimales ?? 0, origine: 'ccas',
    }
    return { ...b, cellule, source_libre: sourceLibre.trim() } satisfies BlocACompleter
  })
}

export function viderDonnee(doc: Document, id: string): Document {
  return remplacerBloc(doc, id, b => {
    if (b.type !== 'a_completer') throw new Error(`${id} n'est pas un bloc à compléter`)
    return { ...b, cellule: null, source_libre: null }
  })
}

/** Les décimales à donner à une saisie : celles de la cellule, ou celles de la saisie si plus fines. */
export function decimalesDe(saisie: string, defaut = 0): number {
  const m = /[,.](\d+)\s*\D*$/.exec(saisie.trim())
  return Math.min(3, Math.max(defaut, m ? m[1].length : 0))
}
